import { api } from '@/api';
import { ActionContext } from 'vuex';
import { IPrescriptionCreate, IPrescriptionUpdate, IPrescription } from '@/interfaces';
import { State } from '../state';
import { PrescriptionState } from './state';
import { getStoreAccessors } from 'typesafe-vuex';
import { commitSetPrescription, commitSetDocumentsForPrescription, commitRemovePrescription, commitSetPrescriptions, commitClearPrescriptions} from './mutations';
import { dispatchCheckApiError } from '../main/actions';
import { commitAddNotification, commitRemoveNotification } from '../main/mutations';
import { IPrescriptionFilter } from '@/interfaces/prescriptions';
import { i18n } from '@/plugins/i18n';

type MainContext = ActionContext<PrescriptionState, State>;

export const actions = {
  async actionGetPrescriptions(context: MainContext) {
    try {
      commitClearPrescriptions(context)
      let skip = 0;
      const limit = 100
      const results: IPrescription[] = []

      while (true){

        const filter: IPrescriptionFilter = {
          skip,
          limit,
          search_text: '',
          exclude_disabled_prescriptions: false,
          exclude_disabled_patients: true,
          no_more_remaining_sessions: false,
          sort_by: 'date',
          sort_order: 'desc',
        }
        const response = await api.getPrescriptions(filter);
        if (response) {
          if (response.data.length == 0){
            break
          }
          results.push(...response.data)
          await commitSetPrescriptions(context, results)
          skip += limit;
        }
      }

    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionUpdatePrescription(context: MainContext, payload: { id: number, prescription: IPrescriptionUpdate }) {
    try {
      const loadingNotification = { content: String(i18n.t('common.save')), showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (await Promise.all([
        api.updatePrescription(payload.id, payload.prescription),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitSetPrescription(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('prescriptions.prescriptionUpdated')), color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionDeletePrescription(context: MainContext, payload: { id: number }) {
    try {
      const loadingNotification = { content: String(i18n.t('common.delete')), showProgress: true };
      commitAddNotification(context, loadingNotification);
      await Promise.all([
        api.deletePrescription(payload.id),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
      commitRemovePrescription(context, payload.id);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('prescriptions.prescriptionDeleted')), color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionCreatePrescription(context: MainContext, payload: IPrescriptionCreate) {
    try {
      const loadingNotification = { content: String(i18n.t('common.save')), showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (await Promise.all([
        api.createPrescription(payload),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitSetPrescription(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('prescriptions.prescriptionCreated')), color: 'success' });
      return response.data
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  // Documents
  async addDocument(context: MainContext, payload: { prescriptionId: number, file: File }) {
    try {
      if (!payload.prescriptionId){
        return
      }
      await api.addDocumentToPrescription(payload.prescriptionId, payload.file);
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async getDocuments(context: MainContext, payload: { prescriptionId: number }) {
    if (payload.prescriptionId == null){
      return
    }
    try {
      const response = await api.getDocumentsForPrescription(payload.prescriptionId);
      if (response) {
        commitSetDocumentsForPrescription(context, {prescriptionId: payload.prescriptionId, docs: response.data});
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  // eHealth
  async getAddressBook(context: MainContext, payload: {
    nihii: string,
  }) {
    try {
      const response = (await Promise.all([
        api.getAddressBook(payload.nihii),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      console.log(response.data)
      return response.data
    } catch (error: any) {
      if (error.response){
        let error_message = error.response.data.detail.message ?? '';
        if (error.response.status == 401){
          error_message = String(i18n.t('attestations.eHealthLoginInvalid'))
        }
        if (error.response.status == 400){
          error_message = String(i18n.t('eHealth.addressBookFetchError'))
        }
        const error_message_detail = error.response.data.detail.detail;
        console.log(error_message)
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      }
    }
  },
  async searchAddressBook(context: MainContext, payload: {
    last_name_search: string,
    profession?: string,
  }) {
    try {
      const response = (await Promise.all([
        api.searchAddressBook(payload.last_name_search, payload.profession),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      console.log(response.data)

      if (response.data.length == 0){
        const error_message = String(i18n.t('eHealth.noSearchResults'))
        commitAddNotification(context, { color: 'error', content: error_message });
      }
      return response.data
    } catch (error: any) {
      if (error.response){
        let error_message = error.response.data.detail.message ?? '';
        if (error.response.status == 401){
          error_message = String(i18n.t('attestations.eHealthLoginInvalid'))
        }
        if (error.response.status == 400){
          error_message = String(i18n.t('eHealth.addressBookSearchError'))
        }
        const error_message_detail = error.response.data.detail.detail;
        console.log(error_message)
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      }
    }
  },
};

const { dispatch } = getStoreAccessors<PrescriptionState, State>('');

export const dispatchCreatePrescription = dispatch(actions.actionCreatePrescription);
export const dispatchGetPrescriptions = dispatch(actions.actionGetPrescriptions);
export const dispatchUpdatePrescription = dispatch(actions.actionUpdatePrescription);
export const dispatchDeletePrescription = dispatch(actions.actionDeletePrescription);

export const dispatchAddDocumentToPrescription = dispatch(actions.addDocument);
export const dispatchGetDocumentsForPrescription = dispatch(actions.getDocuments);
export const dispatchGetAddressBook = dispatch(actions.getAddressBook)
export const dispatchSearchAddressBook = dispatch(actions.searchAddressBook)
